import React from 'react';

import { StyleUtil } from 'ui/util';
import classNames from './EABDDesktopStepIndicator.module.scss';

export interface DesktopStepIndicatorProps
  extends Readonly<{
    numberOfItems: number;
    currentIndex: number;
    getLabelForIndex: (index: number) => string;
    getDescriptionForIndex: (index: number) => string;
  }> {}

export const DesktopStepIndicator = React.memo<DesktopStepIndicatorProps>((props) => {
  const { numberOfItems, currentIndex, getLabelForIndex, getDescriptionForIndex } = props;

  const NodeIterator = React.useMemo(() => Array.from({ length: numberOfItems }), [numberOfItems]);
  const DescriptionIterator = React.useMemo(() => Array.from({ length: numberOfItems - 1 }), [numberOfItems]);

  const RenderNodeCallback = React.useCallback(
    (_item: unknown, index: number) => {
      const isActive = currentIndex >= index;
      const isHighlighted = currentIndex - 1 === index;
      const isFocused = currentIndex === index;

      const nodeInnerClassNames: string[] = [classNames.inner];
      const labelClassNames: string[] = [classNames.label];

      if (isActive) {
        nodeInnerClassNames.push(classNames.active);
        labelClassNames.push(classNames.active);
      }

      if (isHighlighted) {
        nodeInnerClassNames.push(classNames.highlighted);
      }

      if (isFocused) {
        labelClassNames.push(classNames.focused);
      }

      return (
        <div key={index} className={classNames.nodeContainer}>
          <div className={classNames.node}>
            <div className={StyleUtil.mergeClassNames(nodeInnerClassNames)} />
          </div>
          <p className={StyleUtil.mergeClassNames(labelClassNames)}>{getLabelForIndex(index)}</p>
        </div>
      );
    },
    [currentIndex, getLabelForIndex]
  );

  const RenderDescriptionCallback = React.useCallback(
    (_item: unknown, index: number) => {
      const descriptionClassNames: string[] = [classNames.description];

      if (currentIndex - 1 === index) {
        descriptionClassNames.push(classNames.active);
      } else if (currentIndex - 1 > index) {
        descriptionClassNames.push(classNames.visited);
      }

      const fullDescription = getDescriptionForIndex(index);
      const beforeLineBreak = fullDescription.slice(0, fullDescription.indexOf('\n'));
      const afterLineBreak = fullDescription.slice(fullDescription.indexOf('\n'), fullDescription.length);

      return (
        <React.Fragment key={index}>
          <p className={StyleUtil.mergeClassNames(descriptionClassNames)}>
            {beforeLineBreak}
            <br></br>
            {afterLineBreak}
          </p>
        </React.Fragment>
      );
    },
    [currentIndex, getDescriptionForIndex]
  );

  const NodeLineContainerElement = React.useMemo(() => {
    const fullWidthPercent = 100;
    const widthPercentPerPage = fullWidthPercent / (numberOfItems - 1);
    const currentWidthPercent = widthPercentPerPage * currentIndex;

    const nodeLineStyle: React.CSSProperties = {
      width: `${currentWidthPercent}%`,
    };

    return (
      <div className={classNames.nodeLineContainer}>
        <div className={classNames.nodeLine} style={nodeLineStyle} />
      </div>
    );
  }, [numberOfItems, currentIndex]);

  const MovingNodeContainerElement = React.useMemo(() => {
    const fullLeftPercent = 100;
    const leftPercentPerPage = fullLeftPercent / (numberOfItems - 1);
    const currentLeftPercent = leftPercentPerPage * currentIndex;
    const nodeOuterSizeInPx = 12;
    const nodeLineThicknessInPx = 1;

    const nodeStyle: React.CSSProperties = {
      left: '0%',
    };

    if (currentIndex > 0) {
      const nodeBaseLeft = -1 * (nodeOuterSizeInPx * (currentLeftPercent / 100)) - nodeLineThicknessInPx;
      nodeStyle.left = `calc(${nodeBaseLeft}px + ${currentLeftPercent}%)`;
    }

    const nodeInnerClassNames: string[] = [classNames.inner, classNames.active];

    return (
      <div className={classNames.movingNodeContainer}>
        <div className={classNames.node} style={nodeStyle}>
          <div className={StyleUtil.mergeClassNames(nodeInnerClassNames)} />
        </div>
      </div>
    );
  }, [numberOfItems, currentIndex]);

  const MovingLineContainerElement = React.useMemo(() => {
    const fullPercent = 100;
    const percentPerPage = fullPercent / (numberOfItems - 1);

    const nodeLineStyle: React.CSSProperties = {};

    if (currentIndex > 0) {
      nodeLineStyle.width = `${percentPerPage}%`;
    }

    if (currentIndex > 1) {
      const currentPercent = percentPerPage * (currentIndex - 1);
      nodeLineStyle.left = `${currentPercent}%`;
    }

    return (
      <div className={classNames.movingLineContainer}>
        <div className={classNames.nodeLine} style={nodeLineStyle} />
      </div>
    );
  }, [numberOfItems, currentIndex]);

  return (
    <div className={classNames.container}>
      {NodeLineContainerElement}
      {MovingLineContainerElement}
      <div className={classNames.nodesContainer}>{NodeIterator.map(RenderNodeCallback)}</div>
      {MovingNodeContainerElement}
      <div className={classNames.descriptionContainer}>{DescriptionIterator.map(RenderDescriptionCallback)}</div>
    </div>
  );
});
